import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight } from './../../../assets/Icons';
import type { ICursorBasedPagination, IPageBasedPagination, IPagination } from './../../../types/content_switchers/pagination';
import { ContentSwitcher } from '../ContentSwitcher';
import { Dropdown } from '../../Dropdown';
import { DropdownButton } from '../../Dropdown/DropdownButton';
import { DropdownMenu } from '../../Dropdown/DropdownMenu';
import { DropdownMenuItem } from '../../Dropdown/DropdownMenuItem';
import { extractPageNumberFromUrl } from '../../../utils/extractPageNumberFromUrl';
import styles from "./pagination.module.scss";

export const Pagination = (props: IPagination) => {

  const {
    type = 'page_based',
    lang = 'en',
    itemsPerPage,
    currentPageSize,
    pageSizeOptions = [...new Set([10, 20, 50, 100])].sort((a, b) => a - b),
    defaultSelectedPage = 1,
    onPageSizeChange,
    onPageChange,
    onNextHandler,
    onPreviousHandler,
    onPageClickHandler,
    localization,
    hideLabels = false,
    canChangePageSize = true
  } = props;

  const { totalItems = 0, maxVisiblePages = 1, compactMode = false } = props as IPageBasedPagination;
  const rawNext = (props as ICursorBasedPagination).next;
  const rawPrev = (props as ICursorBasedPagination).prev;

  const next = extractPageNumberFromUrl(rawNext);
  const prev = extractPageNumberFromUrl(rawPrev, true);

  const ar = lang === 'ar';

  const { of: Tof, items: Titems, next: Tnext, previous: Tprev, show: Tshow } = {
    of: localization?.of || (ar ? "من" : "of"),
    items: localization?.items || (ar ? "عنصر" : "items"),
    next: localization?.next || (ar ? "التالي" : "Next"),
    previous: localization?.previous || (ar ? "السابق" : "Previous"),
    show: localization?.show || (ar ? "اعرض" : "Show")
  };

  const [pageSize, setPageSize] = useState(currentPageSize || pageSizeOptions[0]);
  const [currentPage, setCurrentPage] = useState(defaultSelectedPage);

  const totalPages = Math.ceil(totalItems / pageSize);

  useEffect(() => {

    if (type === "cursor_based") {

      if (!next && !prev) {
        setCurrentPage(1);
      } else if (next) {
        setCurrentPage(next - 1);
      } else if (prev) {
        setCurrentPage(prev + 1);
      };
    } else {
      (currentPage > totalPages) && setCurrentPage(totalPages);
    }

  }, [pageSize]);

  useEffect(() => {
    onPageChange?.(currentPage);
  }, [currentPage]);

  const paginationRange = useMemo(() => {
    if (type === 'page_based') {
      if (compactMode) {
        //  Compact mode (Mobile) Pagination Logic (Show Only 3 Pages)
        if (totalPages <= 3) {
          return Array.from({ length: totalPages }, (_, i) => i + 1);
        };

        if (currentPage === 1) {
          return [1, 2, 3];
        };

        if (currentPage === totalPages) {
          return [totalPages - 2, totalPages - 1, totalPages];
        };

        return [currentPage - 1, currentPage, currentPage + 1];

      } else {

        // Display all pages without dots if they're less than "maxVisiblePages"
        if (totalPages <= maxVisiblePages) {
          return Array.from({ length: totalPages }, (_, i) => i + 1);
        };

        // Display start pages and one end page separated by dots in case the user is closer to the start
        // ex: maxVisiblePages = 5
        // 1,2,3,4,...,{last page}
        //  "maxVisiblePages - 2" ---> X,X,X,O,...O  "2": are the last page and the last page in the start pages list
        if (currentPage <= maxVisiblePages - 2) {
          return [...Array.from({ length: maxVisiblePages - 1 }, (_, i) => i + 1), "...", totalPages];
        };

        // Display end pages and one start page separated by dots in case the user is closer to the start
        // ex: maxVisiblePages = 5
        // 1,...,{4 last pages}
        //  "maxVisiblePages - 2" ---> O,...,O,X,X,X  "2": are the start page and the start page in the end pages list
        if (currentPage > totalPages - (maxVisiblePages - 2)) {
          return [1, "...", ...Array.from({ length: maxVisiblePages - 1 }, (_, i) => totalPages - (maxVisiblePages - 2) + i)];
        } else {

          const firstPage = 1;
          const lastPage = totalPages;

          // midPagesNum = maxVisiblePages - start page - end page
          // {start page},...,{maxVisiblePages - 2},...,{end page}
          const midPagesNum = maxVisiblePages - 2;


          // pagesAroundCurrentPage = (midPagesNum - current page) / 2
          const pagesAroundCurrentPage = Math.floor((midPagesNum - 1) / 2);
          let midPages = [];
          let beforeCurrent = [];
          let afterCurrent = [];

          // Add pages around current page
          for (let i = pagesAroundCurrentPage; i >= 1; i--) {
            beforeCurrent.push(currentPage - i);
            afterCurrent.push(currentPage + i);
          };

          midPages = [...beforeCurrent, currentPage, ...afterCurrent].sort((a, b) => a - b);

          // Show currentPage and pages around it
          return [firstPage, "...", ...midPages, "...", lastPage];
        }
      };
    } else {
      const nextPage = next ? [currentPage + 1] : [];
      const prevPage = (prev && (currentPage - 1 > 0)) ? [currentPage - 1] : [];

      return [...prevPage, currentPage, ...nextPage];
    }

  }, [currentPage, totalPages, maxVisiblePages, compactMode, next, prev]);

  const goPrev = () => {
    setCurrentPage((currentPage) => {
      onPreviousHandler?.(currentPage - 1);
      return (currentPage - 1);
    });
  };

  const goNext = () => {
    setCurrentPage((currentPage) => {
      onNextHandler?.(currentPage + 1);
      return (currentPage + 1);
    });
  };

  const handlePageSizeOptionClick = (option: number) => {
    setPageSize(option);
    onPageSizeChange?.(option);
  };

  const handlePageClick = (pageNum: number) => {
    setCurrentPage(pageNum);
    onPageClickHandler?.(pageNum);
  };

  return (
    <div className={styles["pagination"]}>

      {
        canChangePageSize &&
        <div className={styles["pagination__items-per-page-box"]}>
          {

            <Dropdown width='fit-content'>
              <DropdownButton
                label={Tshow}
                text={pageSize.toString()}
              />
              <DropdownMenu verticalPlacement='top'>
                {
                  pageSizeOptions.map((option, index) =>
                    <DropdownMenuItem
                      key={`page-size-option-${index}`}
                      text={option.toString()}
                      selected={option === pageSize}
                      onClickHandler={() => handlePageSizeOptionClick(option)}
                    />
                  )
                }
              </DropdownMenu>
            </Dropdown>
          }

          {
            totalItems ?
              <div className={styles["pagination__items-per-page-box__items-count"]}>
                {`
              ${itemsPerPage ?
                    itemsPerPage
                    : (pageSize > totalItems) ?
                      totalItems
                      :
                      pageSize
                  } 
                ${Tof} 
                ${totalItems} ${Titems}`}
              </div>
              :
              <></>
          }

        </div>
      }

      {((type === 'page_based' && totalPages > 1) || type === 'cursor_based') && <ContentSwitcher
        tabsProps={[
          ...(
            onPreviousHandler ? [{
              text: hideLabels ? '' : Tprev,
              active: false,
              leadingIcon: ar ? ArrowRight : ArrowLeft,
              onClickHandler: goPrev,
              disabled: (
                type === "page_based" ?
                  currentPage === 1
                  :
                  !prev
              )
            }] : []
          ),

          ...(
            paginationRange.map((pageNum) => (
              typeof pageNum === "number" ?
                {
                  text: pageNum.toString(),
                  active: currentPage === pageNum,
                  onClickHandler: () => handlePageClick(pageNum),
                }
                :
                {
                  text: pageNum
                }
            ))
          ),

          ...(
            onNextHandler ? [{
              text: hideLabels ? '' : Tnext,
              active: false,
              trailingIcon: ar ? ArrowLeft : ArrowRight,
              onClickHandler: goNext,
              disabled: (
                type === "page_based" ?
                  currentPage === totalPages
                  :
                  !next
              )
            }] : []
          ),
        ]}
      />
      }
    </div>
  )
};